import { useEffect, useState } from "react";
import "../assets/styles/main.css";

/* ── SVG Icons ── */
const ArrowDiag = () => (
  <svg width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24">
    <path d="M7 17L17 7" />
    <path d="M7 7h10v10" />
  </svg>
);

const ArrowRight = () => (
  <svg width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24">
    <path d="M5 12h14M12 5l7 7-7 7" />
  </svg>
);

/* ── Data ── */
const STATS = [
  { value: "200", sup: "+", label: "Engagements delivered" },
  { value: "98",  sup: "%", label: "Client retention rate" },
  { value: "40",  sup: "+", label: "Consultants on staff" },
  { value: "6",   sup: "+", label: "Years of enterprise work" },
];

const PILLARS = [
  {
    title: "Execution focus",
    text: "Strategy means nothing without implementation. We stay through the hard parts and ensure recommendations actually land.",
  },
  {
    title: "Accountability",
    text: "We measure outcomes, not outputs. Every engagement is defined by clear targets your organization can hold us to.",
  },
  {
    title: "Enterprise scale",
    text: "We've operated in complex environments with competing priorities. We know how decisions get made — and how to move them forward.",
  },
  {
    title: "Long-term thinking",
    text: "Quick wins matter. But we design every solution with sustainability in mind — so the improvement holds after we leave.",
  },
];

const SERVICES = [
  {
    num: "01 —",
    name: "Management Consulting",
    desc: "Executive alignment, organizational design, and strategic planning for leadership teams navigating complex growth or change.",
  },
  {
    num: "02 —",
    name: "Operations Management",
    desc: "End-to-end operational redesign — from process mapping to performance management systems that scale.",
  },
  {
    num: "03 —",
    name: "Logistics & Supply Chain",
    desc: "Vendor strategy, inventory optimization, fulfillment efficiency, and distribution network design for enterprise supply chains.",
  },
  {
    num: "04 —",
    name: "Strategic Planning",
    desc: "Market analysis, competitive positioning, and multi-year roadmapping built on real data — not slides and assumptions.",
  },
  {
    num: "05 —",
    name: "Process Improvement",
    desc: "Systematic identification and elimination of waste, bottlenecks, and redundancy across business-critical workflows.",
  },
  {
    num: "06 —",
    name: "Technology Advisory",
    desc: "Guiding enterprise technology decisions, digital transformation programs, and IT alignment with business strategy.",
  },
];

/* ── Main Component ── */
export default function Westcast24() {
  const [formSubmitted, setFormSubmitted] = useState(false);

  /* Scroll reveal */
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) {
            e.target.classList.add("in");
            observer.unobserve(e.target);
          }
        });
      },
      { threshold: 0.07 }
    );
    document.querySelectorAll(".reveal").forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, []);

  const handleSubmit = (e) => {
    e.preventDefault();
    setFormSubmitted(true);
    setTimeout(() => {
      setFormSubmitted(false);
      e.target.reset();
    }, 4000);
  };

  return (
    <>
      {/* ── NAV ── */}
      <nav className="nav">
        <a href="#" className="nav__logo">
          Westcast<span>-24</span>
        </a>
        <ul className="nav__links">
          <li><a href="#about">About</a></li>
          <li><a href="#services">Services</a></li>
          <li><a href="#contact">Contact</a></li>
        </ul>
        <a href="#contact" className="nav__cta">Start a Conversation</a>
      </nav>

      {/* ── HERO ── */}
      <section className="hero">
        <div className="hero__bar" />
        <div className="hero__bg-text">W24</div>
        <div className="hero__content">
          <div className="hero__eyebrow">
            <div className="hero__eyebrow-line" />
            <span>Business Consulting &amp; Operations Management — Orlando, FL</span>
          </div>
          <h1 className="hero__h1">
            WE MOVE<br />
            <span className="blue">ENTERPRISES</span><br />
            <span className="outline">FORWARD</span>
          </h1>
          <div className="hero__bottom">
            <p className="hero__desc">
              Westcast-24 partners with{" "}
              <strong>enterprise leadership teams</strong> to sharpen strategy,
              streamline operations, and optimize logistics — delivering
              measurable outcomes at scale.
            </p>
            <div className="hero__actions">
              <a href="#services" className="btn-primary">
                Our Services <ArrowDiag />
              </a>
              <a href="#about" className="btn-secondary">
                Learn more <ArrowRight />
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* ── STATS ── */}
      <div className="stats-bar reveal">
        {STATS.map((s) => (
          <div className="stats-bar__cell" key={s.label}>
            <div className="stats-bar__number">
              {s.value}<sup>{s.sup}</sup>
            </div>
            <div className="stats-bar__label">{s.label}</div>
          </div>
        ))}
      </div>

      {/* ── ABOUT ── */}
      <section className="about" id="about">
        <div className="about__left reveal">
          <div>
            <div className="section-label">Who we are</div>
            <div className="about__big">
              BUILT<br />FOR THE<br />
              <span className="dim">HARDEST</span><br />PROBLEMS
            </div>
          </div>
          <p className="about__tagline">
            We don't consult from the sidelines. We embed, analyze, and execute
            — alongside your teams.
          </p>
        </div>

        <div className="about__right reveal">
          <p className="about__body">
            Westcast-24, LLC is a{" "}
            <strong>US-registered management consulting and operations firm</strong>{" "}
            headquartered in Orlando, Florida. We work exclusively with enterprise
            organizations that require more than recommendations — they need results.
          </p>
          <p className="about__body">
            Our consultants bring operational fluency across industries, combining
            rigorous analysis with ground-level execution capability. From
            restructuring supply chains to building the management systems that
            drive consistent performance, we go as deep as the problem demands.
          </p>
          <p className="about__body">
            We are selective about the engagements we take on. That selectivity
            means when we commit to a client,{" "}
            <strong>we commit fully</strong> — with the expertise, bandwidth, and
            accountability the work deserves.
          </p>

          <div className="about__pillars">
            {PILLARS.map((p) => (
              <div className="pillar" key={p.title}>
                <div className="pillar__title">{p.title}</div>
                <div className="pillar__text">{p.text}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── SERVICES ── */}
      <section className="services" id="services">
        <div className="services__head reveal">
          <div className="services__title">
            OUR<br /><span>SERVICES</span>
          </div>
          <p className="services__note">
            Six core practice areas, each staffed by specialists who have
            operated inside the industries they advise.
          </p>
        </div>

        <div className="services__list reveal">
          {SERVICES.map((s) => (
            <div className="svc-row" key={s.num}>
              <div className="svc-row__num">{s.num}</div>
              <div className="svc-row__name">{s.name}</div>
              <div className="svc-row__desc">{s.desc}</div>
              <div className="svc-row__arrow">↗</div>
            </div>
          ))}
        </div>
      </section>

      {/* ── CONTACT ── */}
      <section className="contact" id="contact">
        <div className="contact__left reveal">
          <div className="section-label">Get in touch</div>
          <div className="contact__form-title">
            START AN<br /><span>ENGAGE</span>MENT
          </div>

          <form className="form" onSubmit={handleSubmit}>
            <div className="form__grid">
              <div className="form__field">
                <label htmlFor="firstName">First Name</label>
                <input id="firstName" type="text" placeholder="Richard" required />
              </div>
              <div className="form__field">
                <label htmlFor="lastName">Last Name</label>
                <input id="lastName" type="text" placeholder="Adams" />
              </div>
            </div>
            <div className="form__grid">
              <div className="form__field">
                <label htmlFor="title">Title</label>
                <input id="title" type="text" placeholder="Chief Operating Officer" />
              </div>
              <div className="form__field">
                <label htmlFor="org">Organization</label>
                <input id="org" type="text" placeholder="Your Company" required />
              </div>
            </div>
            <div className="form__field">
              <label htmlFor="email">Email</label>
              <input id="email" type="email" placeholder="r.adams@company.com" required />
            </div>
            <div className="form__field">
              <label htmlFor="service">Service Area</label>
              <select id="service">
                <option value="">Select a practice area…</option>
                <option>Management Consulting</option>
                <option>Operations Management</option>
                <option>Logistics &amp; Supply Chain</option>
                <option>Strategic Planning</option>
                <option>Process Improvement</option>
                <option>Technology Advisory</option>
                <option>Multiple areas</option>
              </select>
            </div>
            <div className="form__field">
              <label htmlFor="message">Describe your challenge</label>
              <textarea
                id="message"
                placeholder="What is your organization working through? What outcomes matter most?"
              />
            </div>
            <div className="form__footer">
              <span className="form__note">Response within one business day</span>
              <button
                type="submit"
                className={`form__btn${formSubmitted ? " form__btn--success" : ""}`}
              >
                {formSubmitted ? (
                  "Received ✓"
                ) : (
                  <>
                    Submit <ArrowDiag />
                  </>
                )}
              </button>
            </div>
          </form>
        </div>

        <div className="contact__right reveal">
          <div>
            <div className="section-label">Direct contact</div>
            <div className="contact__big">
              TALK TO<br />
              <span className="blue">US</span><br />
              <span className="dim">DIRECTLY</span>
            </div>
          </div>

          <div className="contact__info-list">
            <div className="contact__info-row">
              <div className="contact__info-key">Email</div>
              <div className="contact__info-val">
                <a href="mailto:office@westcast-24.com">office@westcast-24.com</a>
              </div>
            </div>
            <div className="contact__info-row">
              <div className="contact__info-key">Phone</div>
              <div className="contact__info-val">
                <a href="tel:+14702062822">+1 (470) 206-2822</a>
              </div>
            </div>
            <div className="contact__info-row">
              <div className="contact__info-key">Address</div>
              <div className="contact__info-val">
                2015 S Tuttle Ave<br />Orlando, FL 34239, USA
              </div>
            </div>
          </div>

          <div className="contact__legal">
            Westcast-24, LLC · EIN 36-4931049<br />
            Doc. No. L18000210348 · State of Florida
          </div>
        </div>
      </section>

      {/* ── FOOTER ── */}
      <footer className="footer">
        <span className="footer__logo">
          Westcast<span>-24</span>
        </span>
        <p className="footer__copy">
          © {getDateRight()} Westcast-24, LLC · All rights reserved · Orlando, FL
        </p>
        <ul className="footer__nav">
          <li><a href="#about">About</a></li>
          <li><a href="#services">Services</a></li>
          <li><a href="#contact">Contact</a></li>
        </ul>
      </footer>
    </>
  );
}

const getDateRight = () => {
  const date = new Date()
  return date.getFullYear()
}